import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Badge,
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import axios, { AxiosRequestConfig } from 'axios'
import { useSelector } from 'react-redux'
import { IUser } from '../../interface/IUser'
import { useRefreshUser } from '../../utils/useRefreshUser'

interface IUserListEntry {
    email: string
    name: string
    personnelNumber: string
}

const MAX_RESULTS = 100
const MIN_SEARCH_LENGTH = 2
const DEBOUNCE_MS = 200

const UserImpersonation = () => {
    const user: IUser = useSelector((state: any) => state.user?.value)
    const { refreshUser } = useRefreshUser()
    const { t } = useTranslation()
    const [dialogOpen, setDialogOpen] = useState(false)
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [userList, setUserList] = useState<IUserListEntry[]>([])
    const [loading, setLoading] = useState(false)
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    const openDialog = async () => {
        setDialogOpen(true)
        if (userList.length === 0) {
            setLoading(true)
            try {
                const config: AxiosRequestConfig = {
                    headers: { Authorization: `Bearer ${user.token}` },
                }
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/UserList`,
                    config
                )
                setUserList(response.data ?? [])
            } catch (error) {
                console.log(error)
            } finally {
                setLoading(false)
            }
        }
    }

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setSearch(value)
        if (debounceTimer.current) clearTimeout(debounceTimer.current)
        debounceTimer.current = setTimeout(() => setDebouncedSearch(value), DEBOUNCE_MS)
    }

    const handleClose = () => {
        setDialogOpen(false)
        setSearch('')
        setDebouncedSearch('')
    }

    const handleSelect = useCallback(async (entry: IUserListEntry) => {
        setDialogOpen(false)
        setSearch('')
        setDebouncedSearch('')
        try {
            const config: AxiosRequestConfig = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                    'Content-Type': 'application/json',
                },
            }
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/Impersonation`,
                { targetEmail: entry.email },
                config
            )
            await refreshUser()
        } catch (error) {
            console.log(error)
        }
    }, [user.token, refreshUser])

    const filtered = useMemo(() => {
        if (debouncedSearch.length < MIN_SEARCH_LENGTH) return []
        const lower = debouncedSearch.toLowerCase()
        const results: IUserListEntry[] = []
        for (const u of userList) {
            if (u.name.toLowerCase().includes(lower) || u.email.toLowerCase().includes(lower)) {
                results.push(u)
                if (results.length >= MAX_RESULTS) break
            }
        }
        return results
    }, [userList, debouncedSearch])

    return (
        <>
            <Tooltip title={t('impersonation.startTooltip')}>
                <IconButton size="large" color="inherit" onClick={openDialog}>
                    <Badge>
                        <ManageAccountsIcon />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{t('impersonation.dialogTitle')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        placeholder={t('impersonation.searchPlaceholder')}
                        value={search}
                        onChange={handleSearchChange}
                        size="small"
                        sx={{ mb: 1, mt: 0.5 }}
                    />
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : debouncedSearch.length < MIN_SEARCH_LENGTH ? (
                        <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
                            {t('impersonation.minCharsHint', { count: MIN_SEARCH_LENGTH })}
                        </Typography>
                    ) : (
                        <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
                            {filtered.map((entry) => (
                                <ListItemButton key={entry.email} onClick={() => handleSelect(entry)}>
                                    <ListItemText
                                        primary={entry.name}
                                        secondary={`${entry.email} · ${entry.personnelNumber}`}
                                    />
                                </ListItemButton>
                            ))}
                            {filtered.length === 0 && (
                                <ListItemText
                                    sx={{ px: 2, color: 'text.secondary' }}
                                    primary={t('impersonation.noResults')}
                                />
                            )}
                            {filtered.length === MAX_RESULTS && (
                                <Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 1, display: 'block' }}>
                                    {t('impersonation.tooManyResults', { count: MAX_RESULTS })}
                                </Typography>
                            )}
                        </List>
                    )}
                </DialogContent>
            </Dialog>
        </>
    )
}

export default UserImpersonation
